import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Award, Zap, Heart, Upload } from 'lucide-react';

function Careers() {
  const [applicationType, setApplicationType] = useState('');
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    position: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create FormData to handle file upload
      const submitData = new FormData();
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone || '');
      submitData.append('applicationType', applicationType);
      submitData.append('companyName', formData.companyName || '');
      submitData.append('position', formData.position || '');
      submitData.append('message', formData.message || '');
      
      // Add file if present
      const fileInput = e.target.querySelector('input[type="file"]');
      if (fileInput && fileInput.files.length > 0) {
        submitData.append('resume', fileInput.files[0]);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/careers`, {
        method: 'POST',
        body: submitData,  // Don't set Content-Type header - browser will set it with boundary
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setApplicationType('');
        setFileName('');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          companyName: '',
          position: '',
          message: ''
        });
        if (fileInput) fileInput.value = '';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileName(e.target.files[0].name);
    } else {
      setFileName('');
    }
  };

  const benefits = [
    {
      icon: <Award className="w-16 h-16" />,
      title: 'Competitive Compensation',
      highlights: ['Industry-leading salaries', 'Comprehensive benefits', 'Performance bonuses', 'Retirement plans']
    },
    {
      icon: <Users className="w-16 h-16" />,
      title: 'Career Growth',
      highlights: ['Clear advancement paths', 'Professional development', 'Leadership training', 'Mentorship programs']
    },
    {
      icon: <Zap className="w-16 h-16" />,
      title: 'Exciting Projects',
      highlights: ['Federal & commercial work', 'Nationwide deployments', 'Mission-critical operations', 'Cutting-edge challenges']
    },
    {
      icon: <Heart className="w-16 h-16" />,
      title: 'Great Culture',
      highlights: ['Excellence-driven team', 'Integrity & respect', 'Work-life balance', 'Collaborative environment']
    }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Join Our Team</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Build Your <span className="text-gold">Career</span> With Us
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto">
            Join a team of dedicated professionals committed to excellence in construction
          </p>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Why <span className="text-gold">Williams Diversified?</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-lg border-2 border-gold/30 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl min-h-[280px] bg-transparent"
              >
                <div className="p-10">
                  <div className="text-gold mb-6 group-hover:scale-110 transition-transform">{benefit.icon}</div>
                  <h4 className="text-white font-bold text-3xl mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                    {benefit.title}
                  </h4>
                  <ul className="text-gray-300 text-lg space-y-2">
                    {benefit.highlights.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Join An <span className="text-gold">Elite Team</span>
            </h3>
            <p className="text-gray-300 text-xl max-w-4xl mx-auto mb-6">
              We don't hire or subcontract just anybody. Williams Diversified maintains the highest standards in the industry—only the best make it through our selection process.
            </p>
            <p className="text-gold text-lg font-semibold max-w-3xl mx-auto">
              If you're a top-tier professional or vendor partner who operates with discipline, integrity, and excellence, we want to hear from you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16 max-w-6xl mx-auto">
            {/* Info Section */}
            <div className="space-y-8">
              <div className="bg-black border border-gold/30 rounded-lg p-8">
                <h4 className="text-2xl font-bold text-gold mb-4">For Elite Professionals</h4>
                <p className="text-white font-semibold mb-3">
                  We don't hire average. We recruit the best.
                </p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Our team operates in high-stakes, mission-critical environments where failure is not an option. We only consider candidates with proven track records, exceptional skills, and the ability to perform under pressure.
                </p>
                <p className="text-gray-300 text-sm mb-4">
                  <span className="text-gold font-semibold">What we look for:</span>
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Proven experience in demanding environments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Certifications and verifiable credentials</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Track record of excellence and reliability</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Ability to deploy rapidly and adapt quickly</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Unwavering commitment to safety and standards</span>
                  </li>
                </ul>
              </div>

              <div className="bg-black border border-gold/30 rounded-lg p-8">
                <h4 className="text-2xl font-bold text-gold mb-4">For Vetted Vendors Only</h4>
                <p className="text-white font-semibold mb-3">
                  Our vendor network is selective by design.
                </p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We partner exclusively with vendors and subcontractors who meet our rigorous standards. If you can't deliver on time, maintain quality under pressure, or communicate clearly in fast-paced environments—don't apply.
                </p>
                <p className="text-gray-300 text-sm mb-4">
                  <span className="text-gold font-semibold">Vendor Requirements:</span>
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Proven capability for rapid mobilization</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Insurance, licensing, and compliance documentation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>References from government or commercial clients</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Demonstrated reliability in high-pressure projects</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">✓</span>
                    <span>Commitment to our standards of excellence</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Application Form */}
            <div className="bg-black border border-gold/30 rounded-lg p-10">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="applicationType" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">I am applying as *</label>
                  <div className="relative">
                    <select
                      id="applicationType"
                      name="applicationType"
                      value={applicationType}
                      onChange={(e) => setApplicationType(e.target.value)}
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors appearance-none cursor-pointer"
                      style={{ 
                        WebkitAppearance: 'none',
                        MozAppearance: 'none'
                      }}
                      required
                    >
                      <option value="" style={{ backgroundColor: '#000', color: '#fff' }}>Select One</option>
                      <option value="employee" style={{ backgroundColor: '#000', color: '#fff' }}>Employee / Job Applicant</option>
                      <option value="vendor" style={{ backgroundColor: '#000', color: '#fff' }}>Vendor / Subcontractor</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gold">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="firstName" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">First Name *</label>
                    <input
                      id="firstName"
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">Last Name *</label>
                    <input
                      id="lastName"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">Email *</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="phone" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">Phone *</label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors"
                    required
                  />
                </div>

                {applicationType === 'vendor' && (
                  <div className="mb-6">
                    <label htmlFor="companyName" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">Company Name *</label>
                    <input
                      id="companyName"
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors"
                      required={applicationType === 'vendor'}
                    />
                  </div>
                )}

                <div className="mb-6">
                  <label htmlFor="position" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                    {applicationType === 'vendor' ? 'Service Category *' : 'Position/Role *'}
                  </label>
                  <input
                    id="position"
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    placeholder={applicationType === 'vendor' ? 'e.g., Equipment Rental, Electrical Contractor, etc.' : 'e.g., Project Manager, Electrician, etc.'}
                    className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors"
                    required
                  />
                </div>

                <div className="mb-8">
                  <label htmlFor="message" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                    {applicationType === 'vendor' ? 'Company Overview *' : 'Cover Letter / Message *'}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="5"
                    placeholder={applicationType === 'vendor' ? 'Tell us about your company, services, and experience...' : 'Tell us about your experience, qualifications, and why you want to join our team...'}
                    className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold resize-none transition-colors"
                    required
                  ></textarea>
                </div>

                <div className="mb-8">
                  <label htmlFor="attachment" className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                    {applicationType === 'vendor' ? 'Attach Company Profile / Credentials *' : 'Attach Resume / CV *'}
                  </label>
                  <div className="relative">
                    <input
                      id="attachment"
                      type="file"
                      name="attachment"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      required
                    />
                    <label
                      htmlFor="attachment"
                      className="flex items-center justify-center w-full bg-black border-2 border-dashed border-gold/30 hover:border-gold rounded-lg px-6 py-8 cursor-pointer transition-all group"
                    >
                      <div className="text-center">
                        <Upload className="w-10 h-10 text-gold mx-auto mb-3 group-hover:scale-110 transition-transform" />
                        {fileName ? (
                          <>
                            <p className="text-gold font-semibold mb-1">{fileName}</p>
                            <p className="text-gray-400 text-sm">Click to change file</p>
                          </>
                        ) : (
                          <>
                            <p className="text-white font-semibold mb-1">Click to upload file</p>
                            <p className="text-gray-400 text-sm">PDF, DOC, or DOCX (Max 5MB)</p>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                  <p className="text-gray-500 text-xs mt-2 italic">
                    {applicationType === 'vendor' 
                      ? 'Include certifications, insurance documentation, and project portfolio' 
                      : 'Must include detailed work history and relevant certifications'}
                  </p>
                </div>

                <div className="bg-black border border-gold/20 rounded-lg p-6 mb-6">
                  <p className="text-gray-300 text-sm text-center leading-relaxed">
                    <span className="text-gold font-semibold">Important:</span> Only complete applications with verifiable credentials and references will be reviewed. Incomplete submissions will not be considered.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full ${isSubmitting ? 'bg-gray-500' : 'bg-gold hover:bg-gold-dark'} text-black font-bold py-5 rounded-lg transition-all transform hover:scale-105 uppercase tracking-wider text-lg`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                </button>
                
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-400 text-center">
                    Thank you for your submission! We will review your application carefully. Only qualified candidates will be contacted.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-400 text-center">
                    Sorry, there was an error submitting your application. Please try again or email us directly.
                  </div>
                )}
                
                <p className="text-gray-500 text-xs text-center mt-4">
                  We only contact qualified candidates. Response time: 5-10 business days.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Our <span className="text-gold">Core Values</span>
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative overflow-hidden rounded-lg border-2 border-gold/30 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl min-h-[200px] bg-transparent">
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/20 transition-colors">
                  <span className="text-gold text-3xl font-bold">1</span>
                </div>
                <h4 className="text-2xl font-bold text-gold mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Excellence</h4>
                <p className="text-gray-300 leading-relaxed">
                  We strive for the highest standards in everything we do—no shortcuts, no excuses
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-lg border-2 border-gold/30 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl min-h-[200px] bg-transparent">
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/20 transition-colors">
                  <span className="text-gold text-3xl font-bold">2</span>
                </div>
                <h4 className="text-2xl font-bold text-gold mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Integrity</h4>
                <p className="text-gray-300 leading-relaxed">
                  Honesty and transparency guide every decision we make and every project we deliver
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-lg border-2 border-gold/30 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl min-h-[200px] bg-transparent">
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/20 transition-colors">
                  <span className="text-gold text-3xl font-bold">3</span>
                </div>
                <h4 className="text-2xl font-bold text-gold mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Teamwork</h4>
                <p className="text-gray-300 leading-relaxed">
                  Collaboration and mutual respect drive our success on every mission
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Careers;